import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Modal } from 'react-bootstrap'
import { useAuth } from '../../modules/auth'
import Swal from 'sweetalert2'
import * as XLSX from 'xlsx'
import './PlanHeader.css'

const PLAN_LIMITS = {
  "Starter Mensual": 30,
  "Starter Anual": 30,
  "Professional Mensual": 100,
  "Professional Anual": 100,
}

export default function PlanHeader({ totalExpedientes, onRefresh }) {
  const API_URL = process.env.REACT_APP_API_URL
  const { currentUser } = useAuth()
  const fecha = new Date()

  // ─── FORMATEO DE FECHA ───
  const formatoFecha = (fecha, formato) => {
    const d = new Date(fecha)
    const dia = d.getDate().toString().padStart(2, '0')
    const mes = (d.getMonth() + 1).toString().padStart(2, '0')
    const anio = d.getFullYear()
    const horas = d.getHours().toString().padStart(2, '0')
    const minutos = d.getMinutes().toString().padStart(2, '0')

    return formato
      .replace('dd', dia)
      .replace('mm', mes)
      .replace('yyyy', anio)
      .replace('HH', horas)
      .replace('MM', minutos)
  }

  const userPlan = currentUser?.plan

  // 🆕 NUEVO (no rompe nada existente)
  const isDemo = currentUser?.subscription === 'Demo'

  const maxRecords = isDemo
    ? Infinity
    : (PLAN_LIMITS[userPlan] || 0)

  const total = Number(totalExpedientes) || 0
  const remaining = isDemo ? Infinity : (maxRecords - total)
  const isLimitReached = isDemo ? false : (remaining <= 0)
  const isAdmin = currentUser?.plan === 'Admin' 

  const [showNewExp, setShowNewExp] = useState(false)
  const [showBulkLoad, setShowBulkLoad] = useState(false)
  const [loadingNewExp, setLoadingNewExp] = useState(false)
  const [file, setFile] = useState(null)
  const [bulkCount, setBulkCount] = useState(0)
  const [stripeSession, setStripeSession] = useState('')

  const handleCloseNewExp = () => setShowNewExp(false)
  const handleShowNewExp = () => setShowNewExp(true)

  const handleCloseBulkLoad = () => {
    setShowBulkLoad(false)
    setBulkCount(0)
    setFile(null)
  }
  const handleShowBulkLoad = () => setShowBulkLoad(true)

  const handleChange = (event) => {
    const file = event.target.files[0]
    setFile(file)

    const reader = new FileReader()
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result)
      const workbook = XLSX.read(data, { type: 'array' })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(sheet)
      setBulkCount(rows.length)
    }
    reader.readAsArrayBuffer(file)
  }

  // ─── NUEVO EXPEDIENTE ───
  const uploadNewExp = () => {
    setLoadingNewExp(true)
    const expName = document.getElementById('new-exp-name').value.replace(/ /g, '')
    const parteActora = document.getElementById('parte-actora').value

    axios.post(API_URL + '/app/newExp', {
      idUser: currentUser.id,
      expName,
      parteActora
    })
      .then(() => {
        if (onRefresh) onRefresh()
        setLoadingNewExp(false)
        handleCloseNewExp()
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Se ha registrado el nuevo expediente",
          showConfirmButton: false,
          timer: 1500
        })
      })
      .catch((err) => {
        setLoadingNewExp(false)

        if (!isAdmin && err?.response?.data?.error === 'PLAN_LIMIT') {
          const { allowed, current, attempted } = err.response.data
          Swal.fire({
            icon: 'error',
            title: 'Límite de plan alcanzado',
            html: `Tu plan permite <b>${allowed}</b> expedientes.<br>
                   Ya tienes <b>${current}</b>.<br>
                   Intentaste agregar <b>${attempted}</b>.`
          })
        } else {
          Swal.fire("Error", "No se pudo crear el expediente", "error")
        }
      })
  }

  // ─── CARGA MASIVA ───
  const uploadBulkLoad = () => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('idUser', currentUser.id)

    setLoadingNewExp(true)

    axios.post(API_URL + '/app/bulkLoad', formData, {
      headers: { 'content-type': 'multipart/form-data' },
    })
      .then(() => {
        if (onRefresh) onRefresh()
        setLoadingNewExp(false)
        handleCloseBulkLoad()
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Se han registrado los expedientes",
          showConfirmButton: false,
          timer: 1500
        })
      })
      .catch((err) => {
        setLoadingNewExp(false)

        if (!isAdmin && err?.response?.data?.error === 'PLAN_LIMIT') {
          const { allowed, current, attempted } = err.response.data
          Swal.fire({
            icon: 'error',
            title: 'Límite de plan alcanzado',
            html: `Tu plan permite <b>${allowed}</b> expedientes.<br>
                   Ya tienes <b>${current}</b>.<br>
                   Intentaste subir <b>${attempted}</b>.`
          })
        } else {
          Swal.fire("Error", "No se pudo procesar el archivo", "error")
        }
      })
  }

  // ─── OBTENER SESIÓN STRIPE ───
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await axios.get(`${API_URL}/app/myProfileHeader/${currentUser?.id}`)
        const sessionID = res.data[0].session_id
        setStripeSession(sessionID)
      } catch (err) {
        console.error("Error obteniendo sesión Stripe", err)
      }
    }

    if (currentUser) fetchSession()
  }, [currentUser])

  return (
    <>
      {/* HEADER */}
      <div className='row gy-5 g-xl-8'>
        <div className='col-lg-5'>
          <h2>Bienvenido</h2>
          <p>{formatoFecha(fecha, 'dd de mm yyyy - HH:MM hrs')}</p>
        </div>

        <div className='col-lg-7 text-right'>
          {/* ALERTA DE PLAN */}
          {!isAdmin && (
            <div
              className={`alert ${
                isLimitReached ? 'alert-danger' : 'alert-info'
              } mb-2 d-flex align-items-center justify-content-between`}
            >
              <div>
                Tu plan <b>{userPlan}</b>: {total} de {isDemo ? '∞' : maxRecords} expedientes usados.
                {!isLimitReached && !isDemo && <> Te quedan <b>{remaining}</b>.</>}
                {isLimitReached && <> Has alcanzado el límite.</>}
                {isDemo && <> (Demo sin límite)</>}
              </div>

              {stripeSession && (
                <form
                  action={API_URL + '/stripe/createPortalSession'}
                  method='POST'
                  className='ms-3 m-0'
                >
                  <input
                    type='hidden'
                    name='session_id'
                    value={stripeSession}
                  />
                  <button
                    type='submit'
                    className='btn btn-sm btn-light-success fw-bolder fs-8 py-1 px-3'
                  >
                    Maneja tu suscripción
                  </button>
                </form>
              )}
            </div>
          )}

          {/* BOTONES */}
          <button
            className='btn btn-primary float-end w-60'
            onClick={handleShowNewExp}
            disabled={!isAdmin && !isDemo && isLimitReached}
          >
            Nuevo expediente
          </button>

          <button
            className='btn float-end w-60 mr-10 btn-bulk-load'
            onClick={handleShowBulkLoad}
            disabled={!isAdmin && !isDemo && isLimitReached}
          >
            Carga masiva de expedientes
          </button>
        </div>
      </div>

   
      {/* MODALES */}
      {/* Modal nuevo expediente */}
      <Modal show={showNewExp} onHide={handleCloseNewExp}>
        <Modal.Header closeButton>
          <Modal.Title>Dar de alta expediente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='row'>
            <div className='form-group col-md-12'>
              <label className='alerta-modal'>
                <strong>
                  Agrega el número de expediente tal y como aparece en el boletín.
                </strong>
              </label>
              <label>Nuevo expediente:</label>
              <input type='text' className='form-control' id='new-exp-name' autoFocus />
            </div>
          </div>
          <br />
          <div className='row'>
            <div className='form-group col-md-12'>
              <label>Parte actora (opcional):</label>
              <input type='text' className='form-control' id='parte-actora' />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-secondary' onClick={handleCloseNewExp}>
            Cerrar
          </button>
          <button type='button' className='btn btn-primary' onClick={uploadNewExp}>
            {!loadingNewExp && <span className='indicator-label'>Guardar</span>}
            {loadingNewExp && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Por favor espere...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modal carga masiva */}
      <Modal show={showBulkLoad} onHide={handleCloseBulkLoad}>
        <Modal.Header closeButton>
          <Modal.Title>Carga masiva de expedientes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='row'>
            <div className='form-group col-md-12'>
              <label className='alerta-modal'><strong>1. Para iniciar, descarga el siguiente formato:</strong></label>
              <a
                href={API_URL + "/formato/Adinte-Carga-Masiva.xlsx"}
                target='_blank'
                className='btn btn-primary btn-download'
              >
                Descargar formato
              </a>
            </div>
          </div>
          <br />
          <div className='row'>
            <div className='form-group col-md-12'>
              <label className='alerta-modal'><strong>2. Una vez descargado, llena los campos y adjúntalo:</strong></label>
              <br />
              <label>Subir formato:</label>
              <input
                type='file'
                className='form-control'
                id='file-bulk-load'
                name='file-bulk-load'
                autoFocus
                accept='.xlsx, .xls'
                required
                onChange={handleChange}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-secondary' onClick={handleCloseBulkLoad}>
            Cerrar
          </button>
          <button type='button' className='btn btn-primary' onClick={uploadBulkLoad}>
            {!loadingNewExp && <span className='indicator-label'>Cargar</span>}
            {loadingNewExp && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Por favor espere...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}